import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { TimelineEntry } from '../features/timeline';
import { getPublicTimeline } from '../features/timeline';
import { getImageUrlById } from '../api/images';
import Footer from '../components/Footer';

// Helper to parse imageIds from JSON
const parseImageIds = (imageIdsJson: string | null): string[] => {
  if (!imageIdsJson) return [];
  try {
    const parsed = JSON.parse(imageIdsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

interface ImageCarouselProps {
  images: string[];
  title: string;
  onPreview?: (src: string, title: string) => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, title, onPreview }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="image-carousel">
      {/* Main Image Display */}
      <div
        className="position-relative rounded overflow-hidden bg-dark image-zoomable"
        style={{ height: 400 }}
        role={onPreview ? 'button' : undefined}
        tabIndex={onPreview ? 0 : undefined}
        onClick={() => {
          if (!onPreview) return;
          const src = images[currentIndex];
          if (!src) return;
          onPreview(src, title);
        }}
        onKeyDown={(e) => {
          if (!onPreview) return;
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          const src = images[currentIndex];
          if (!src) return;
          onPreview(src, title);
        }}
      >
        <img
          src={images[currentIndex]}
          alt={`${title} - ${currentIndex + 1} of ${images.length}`}
          className="w-100 h-100"
          style={{ objectFit: 'cover' }}
          loading="eager"
          decoding="async"
          onError={(e) => {
            const img = e.currentTarget;
            img.style.display = 'none';
            const placeholder = img.parentElement?.querySelector('.timeline-carousel-fallback') as HTMLElement | null;
            if (placeholder) placeholder.style.display = 'flex';
          }}
        />
        <span className="image-zoom-hint">
          <i className="bi bi-fullscreen" /> Click to zoom
        </span>
        <div
          className="timeline-carousel-fallback"
          style={{
            display: 'none',
            position: 'absolute',
            inset: 0,
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.35)',
          }}
        >
          <i className="bi bi-image text-white" style={{ fontSize: '3rem' }} />
        </div>
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              className="btn btn-light position-absolute start-0 top-50 translate-middle-y ms-2 rounded-circle p-2"
              onClick={goToPrevious}
              style={{ width: 44, height: 44 }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <button
              className="btn btn-light position-absolute end-0 top-50 translate-middle-y me-2 rounded-circle p-2"
              onClick={goToNext}
              style={{ width: 44, height: 44 }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
          <span className="badge bg-dark bg-opacity-75 px-3 py-2">
            {currentIndex + 1} / {images.length}
          </span>
        </div>

        {/* Toggle Thumbnails Button */}
        {images.length > 1 && (
          <button
            className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
            onClick={() => setShowThumbnails(!showThumbnails)}
          >
            <i className={`bi ${showThumbnails ? 'bi-grid-3x2-gap-fill' : 'bi-grid-3x2-gap'}`}></i>
          </button>
        )}
      </div>

      {/* Thumbnails Strip */}
      {images.length > 1 && (
        <div className={`mt-3 ${showThumbnails ? 'd-block' : 'd-none d-md-block'}`}>
          <div className="d-flex gap-2 overflow-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
            {images.map((img, idx) => (
              <button
                key={idx}
                className={`btn p-0 border-2 ${idx === currentIndex ? 'border-primary' : 'border-transparent'}`}
                onClick={() => goToSlide(idx)}
                style={{ minWidth: 80, height: 60, position: 'relative' }}
              >
                <img
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-100 h-100"
                  style={{ objectFit: 'cover', opacity: idx === currentIndex ? 1 : 0.6 }}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    const el = e.currentTarget;
                    el.style.display = 'none';
                    const placeholder = el.parentElement?.querySelector(
                      '.timeline-thumbnail-fallback'
                    ) as HTMLElement | null;
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                <div
                  className="timeline-thumbnail-fallback"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)',
                  }}
                >
                  <i className="bi bi-image text-white" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TimelinePage: React.FC = () => {
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [zoomedImageTitle, setZoomedImageTitle] = useState<string>('');
  const yearButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  useEffect(() => {
    setIsLoading(true);
    getPublicTimeline()
      .then((data) => {
        setEntries(data);
        if (data.length > 0) {
          setSelectedYear((prev) => prev ?? data[0].year);
        }
      })
      .catch((err) => {
        console.error('Failed to load timeline:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);
  
  const selectedEntry = useMemo(() => {
    if (entries.length === 0) return null;
    return entries.find(entry => entry.year === selectedYear) || entries[0];
  }, [entries, selectedYear]);

  const sortedEntries = useMemo(() => 
    [...entries].sort((a, b) => a.year - b.year),
    [entries]
  );

  const isEmptyState = !isLoading && entries.length === 0;

  const selectedImageIds = useMemo(() => {
    if (!selectedEntry) return [];
    return parseImageIds(selectedEntry.imageIdsJson);
  }, [selectedEntry]);

  const hasTimelineImages = selectedImageIds.length > 0;

  useEffect(() => {
    if (selectedYear == null) return;
    const el = yearButtonRefs.current.get(selectedYear);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selectedYear]);

  return (
    <>
      <main className={`${isEmptyState ? '' : 'min-vh-100'} bg-gradient-light`}>
        {/* Header Section */}
        <div className="bg-primary text-white py-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center">
                <span className="badge bg-white bg-opacity-25 text-white mb-3">Our Heritage</span>
                <h1 className="display-4 fw-bold mb-3">History of Bothasig Masjid</h1>
                <p className="lead mb-0 opacity-75">
                  A journey of faith, community, and growth
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="container py-5 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">Loading timeline...</p>
          </div>
        )}

        {/* Empty State */}
        {isEmptyState && (
          <div className="container py-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 p-lg-5 text-center">
                <i className="bi bi-clock-history text-muted" style={{ fontSize: '3rem' }}></i>
                <h2 className="h5 mt-3 mb-1">No timeline entries yet</h2>
                <p className="text-muted mb-0">
                  When masjid history entries are added, they will appear here.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Navigation */}
        {!isEmptyState && (
          <div
            className="sticky-top bg-white border-bottom shadow-sm"
            style={{ top: 0, zIndex: 100 }}
          >
            <div className="container py-3 timeline-container">
              <div
                className="timeline-scroll"
                role="navigation"
                aria-label="Timeline years"
              >
                <div className="timeline-scroll__track position-relative">
                  <div className="position-absolute bg-primary timeline-scroll__line" aria-hidden="true" />

                  <div className="d-flex timeline-nodes-row position-relative align-items-start">
                  {sortedEntries.map((entry) => (
                    <button
                      key={entry.year}
                      ref={(el) => {
                        if (el) yearButtonRefs.current.set(entry.year, el);
                        else yearButtonRefs.current.delete(entry.year);
                      }}
                      className={`btn btn-timeline-node p-0 ${selectedYear === entry.year ? 'active' : ''}`}
                      onClick={() => setSelectedYear(entry.year)}
                      aria-label={`View ${entry.year}: ${entry.title}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {/* Year Node Circle */}
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center transition-all ${
                          selectedYear === entry.year
                            ? 'bg-primary text-white shadow'
                            : 'bg-white text-primary border border-2 border-primary'
                        }`}
                        style={{
                          width: selectedYear === entry.year ? 46 : 44,
                          height: selectedYear === entry.year ? 46 : 44,
                          minWidth: selectedYear === entry.year ? 46 : 44,
                          transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                        }}
                      >
                        <span style={{ fontSize: '0.7rem', fontWeight: 700 }}>{entry.year}</span>
                      </div>

                      {/* Year Title - Desktop Only */}
                      <span
                        className={`small fw-medium text-center d-none d-md-block ${
                          selectedYear === entry.year ? 'text-primary' : 'text-muted'
                        }`}
                        style={{
                          maxWidth: 90,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {entry.title}
                      </span>
                    </button>
                  ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Section */}
        {selectedEntry && (
          <div className="container py-5">
            <div className="row g-5">
              {/* Left Column - Description */}
              <div className={hasTimelineImages ? 'col-lg-5' : 'col-12'}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4 p-lg-5">
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div 
                        className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold"
                        style={{ width: 64, height: 64, fontSize: '1.5rem' }}
                      >
                        {selectedEntry.year}
                      </div>
                      <div>
                        <h2 className="h3 mb-1">{selectedEntry.title}</h2>
                      </div>
                    </div>
                    
                    <div 
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: selectedEntry.descriptionHtml || '' }}
                      style={{ 
                        lineHeight: 1.8,
                        fontSize: '1.05rem',
                      }}
                    />
                    
                  </div>
                </div>
              </div>

              {/* Right Column - Image Carousel (only when entry has images) */}
              {hasTimelineImages && (
                <div className="col-lg-7">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-0">
                      <ImageCarousel
                        images={selectedImageIds.map(getImageUrlById).filter(Boolean)}
                        title={selectedEntry.title}
                        onPreview={(src, imageTitle) => {
                          setZoomedImage(src);
                          setZoomedImageTitle(imageTitle);
                        }}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted small">
                      <i className="bi bi-images me-1"></i>
                      {selectedImageIds.length} photos from {selectedEntry.year}
                    </span>
                    <span className="text-muted small">Click thumbnails below to browse</span>
                  </div>
                </div>
              )}
            </div>

          {/* Year Navigation */}
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-center gap-3">
              {/* Previous Year Button - Hidden if no previous */}
              {(() => {
                const currentIndex = sortedEntries.findIndex(e => e.year === selectedYear);
                const hasPrevious = currentIndex > 0;
                return hasPrevious ? (
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => setSelectedYear(sortedEntries[currentIndex - 1].year)}
                  >
                    <i className="bi bi-arrow-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>
                ) : <div style={{ width: 100 }}></div>;
              })()}

              {/* Current Year Display */}
              <div className="bg-primary text-white px-4 py-2 rounded-pill d-flex align-items-center gap-2">
                <i className="bi bi-calendar3"></i>
                <span className="fw-bold">{selectedYear}</span>
              </div>

              {/* Next Year Button - Hidden if no next */}
              {(() => {
                const currentIndex = sortedEntries.findIndex(e => e.year === selectedYear);
                const hasNext = currentIndex < sortedEntries.length - 1;
                return hasNext ? (
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => setSelectedYear(sortedEntries[currentIndex + 1].year)}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="bi bi-arrow-right"></i>
                  </button>
                ) : <div style={{ width: 100 }}></div>;
              })()}
            </div>

            {/* Year Progress Dots */}
            <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
              {sortedEntries.map((entry) => (
                <button
                  key={entry.year}
                  className={`btn p-0 rounded-circle transition-all ${
                    selectedYear === entry.year 
                      ? 'bg-primary' 
                      : 'bg-secondary bg-opacity-25'
                  }`}
                  onClick={() => setSelectedYear(entry.year)}
                  style={{
                    width: selectedYear === entry.year ? 12 : 8,
                    height: selectedYear === entry.year ? 12 : 8,
                    border: 'none',
                    minWidth: 'auto',
                    transition: 'all 0.3s ease',
                  }}
                  aria-label={`Go to ${entry.year}`}
                />
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Back Button */}
        <div className={`container ${isEmptyState ? 'pb-4' : 'pb-5'}`}>
          <div className="text-center">
            <Link to="/masjid" className="btn btn-outline-primary btn-lg">
              <i className="bi bi-arrow-left me-2"></i>
              Back to Masjid
            </Link>
          </div>
        </div>
      </main>
      {/* Image Zoom Lightbox (shared UI like Home) */}
      {zoomedImage && (
        <div
          className="modal show d-block image-zoom-lightbox"
          style={{ zIndex: 1060 }}
          onClick={() => setZoomedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image view"
        >
          <div className="image-zoom-lightbox__content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-zoom-lightbox__close"
              onClick={() => setZoomedImage(null)}
              aria-label="Close zoomed image"
            >
              <i className="bi bi-x-lg" />
            </button>
            <img src={zoomedImage} alt={zoomedImageTitle} className="image-zoomed-img" />
            {zoomedImageTitle && (
              <p className="image-zoom-lightbox__title">{zoomedImageTitle}</p>
            )}
          </div>
        </div>
      )}
      <Footer />

      {/* Custom Styles */}
      <style>{`
        .bg-gradient-light {
          background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
        }
        
        .btn-timeline-node {
          transition: all 0.3s ease;
        }
        
        .btn-timeline-node:hover {
          transform: translateY(-3px);
        }
        
        .btn-timeline-node.active {
          transform: translateY(-5px);
        }
        
        .transition-all {
          transition: all 0.3s ease;
        }

        .timeline-scroll {
          overflow-x: auto;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 4px;
          margin-left: -0.5rem;
          margin-right: -0.5rem;
          padding-left: 0.5rem;
          padding-right: 0.5rem;
        }

        .timeline-scroll__track {
          width: max-content;
          box-sizing: border-box;
        }

        .timeline-scroll__line {
          height: 3px;
          left: 22px;
          right: 22px;
          top: 22px;
          z-index: 1;
        }

        .timeline-nodes-row {
          z-index: 2;
          flex-wrap: nowrap;
        }

        @media (min-width: 768px) {
          .timeline-scroll {
            overflow-x: visible;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
            display: flex;
            justify-content: center;
          }

          .timeline-nodes-row {
            justify-content: flex-start;
            gap: 1rem;
          }
        }

        @media (max-width: 767px) {
          .timeline-nodes-row {
            justify-content: flex-start;
            gap: 0.75rem;
          }

          .btn-timeline-node {
            flex-shrink: 0;
          }
        }

        .rich-text-content p {
          margin-bottom: 1rem;
          word-break: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          max-width: 100%;
        }
        
        .rich-text-content p:last-child {
          margin-bottom: 0;
        }

        .rich-text-content * {
          max-width: 100%;
          word-break: break-word;
          overflow-wrap: break-word;
          white-space: normal;
        }

        /* Timeline Mobile Optimizations */
        @media (max-width: 767px) {
          .btn-timeline-node span.small {
            display: none !important;
          }
          
          .btn-timeline-node > div {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px;
          }
          
          .btn-timeline-node > div span {
            font-size: 0.65rem !important;
            font-weight: 700 !important;
          }

          /* Better spacing for mobile timeline */
          .timeline-container {
            padding-left: 8px;
            padding-right: 8px;
          }

          .timeline-scroll__line {
            top: 22px;
            left: 22px;
            right: 22px;
          }
        }

        @media (max-width: 576px) {
          .btn-floating-donate {
            bottom: 15px !important;
            right: 15px !important;
            padding: 14px 18px !important;
          }

          .donors-panel {
            right: 10px !important;
            left: 10px !important;
            width: auto !important;
            max-width: calc(100vw - 20px) !important;
            bottom: 85px !important;
          }

          .donors-panel .p-4 {
            padding: 1rem !important;
          }

          .donors-panel-content {
            max-width: 100% !important;
          }

          /* Even smaller timeline on very small screens */
          .btn-timeline-node > div {
            width: 38px !important;
            height: 38px !important;
            min-width: 38px;
          }

          .btn-timeline-node > div span {
            font-size: 0.6rem !important;
          }

          .timeline-scroll__line {
            top: 19px;
            left: 19px;
            right: 19px;
          }
        }

        /* Timeline node hover effects */
        .btn-timeline-node:hover {
          transform: translateY(-2px);
        }
        
        .btn-timeline-node.active {
          transform: translateY(-4px);
        }

        /* Smooth transitions */
        .transition-all {
          transition: all 0.3s ease;
        }
      `}</style>
    </>
  );
};

export default TimelinePage;
